import { ChevronLeft } from "@untitledui/icons";
import { useState } from "react";
import {
	type To,
	useLocation,
	useNavigate,
	useNavigationType,
} from "react-router";

import { Button } from "@/components/ui/buttons/button";
import { canPopInAppHistory, getHistoryIdx } from "@/routing";

type HeaderBackButtonProps = {
	/** Fallback when this tab has no in-app history (bookmark / direct URL). */
	path?: To;
};

type HeaderBackVisit = {
	historyIdx: number | undefined;
	navigationType: ReturnType<typeof useNavigationType>;
	pathname: string;
};

const captureVisit = (
	navigationType: ReturnType<typeof useNavigationType>,
	pathname: string,
): HeaderBackVisit => ({
	historyIdx: getHistoryIdx(),
	navigationType,
	pathname,
});

export const HeaderBackButton = ({ path }: HeaderBackButtonProps) => {
	const navigate = useNavigate();
	const navigationType = useNavigationType();
	const { pathname } = useLocation();

	/**
	 * How the user arrived at *this* page, captured once per page.
	 *
	 * `useNavigationType` is live and app-wide, so any later replace — on this
	 * page or elsewhere in the app — would overwrite it. Freezing it per
	 * pathname keeps "how did I get here" from being rewritten by what the user
	 * does once they are here, while still re-reading it when they move to a
	 * different page without unmounting (alert A to alert B).
	 *
	 * `historyIdx` is frozen with the same visit so later tab pushes (which
	 * advance Router idx) can still be walked with Back after a REPLACE
	 * arrival, instead of always jumping to `path`.
	 *
	 * Known narrow edge: this button mounts after isLoading clears, while
	 * tabs can render earlier. A REPLACE arrival where the user clicks a tab
	 * before the header mounts captures PUSH at the already-advanced idx, so
	 * walking Back can pop one step past the REPLACE'd entry. Requires a fast
	 * click during loading on a post-login redirect; consequence is one extra
	 * Back step. Left as-is.
	 */
	const [visit, setVisit] = useState(() =>
		captureVisit(navigationType, pathname),
	);

	if (visit.pathname !== pathname) {
		setVisit(captureVisit(navigationType, pathname));
	}

	const handleBack = () => {
		// A REPLACE swapped this page into an existing history entry, so the
		// entry behind the arrival is whatever the user was doing beforehand —
		// somewhere unrelated. Prefer the caller's path (the list they came
		// from) only while we are still on that same history entry.
		if (path !== undefined && visit.navigationType === "REPLACE") {
			const currentIdx = getHistoryIdx();
			// Tab changes (and other in-page pushes) after landing advance idx.
			// Walk those first so Back matches the browser and the rest of the
			// app; only jump to `path` when nothing has been pushed since we
			// arrived.
			if (
				currentIdx !== undefined &&
				visit.historyIdx !== undefined &&
				currentIdx > visit.historyIdx
			) {
				navigate(-1);
				return;
			}
			navigate(path);
			return;
		}

		// Pop when Router idx confirms an in-app predecessor, or when there is
		// no fallback. Otherwise use `path` (e.g. origin) so we stay in-app.
		if (canPopInAppHistory() || path === undefined) {
			navigate(-1);
		} else {
			navigate(path);
		}
	};

	return (
		<Button
			aria-label="Back"
			color="secondary"
			iconLeading={ChevronLeft}
			noBorders
			noTextPadding
			onClick={handleBack}
			size="sm"
		/>
	);
};
